import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import apiUrls from "../../../api";
import { IBuild } from "../../../model/model";
import ModalShell from "../../deviceInstaller/components/ModalShell";
import PlatformIcon from "../../../common/components/Icon/PlatformIcon/PlatformIcon";
import { copyToClipboard } from "../../../common/utils/clipboard";
import SegmentedControl from "../../../common/components/SegmentedControl/SegmentedControl";
import EmailChipsInput, { EmailChip } from "./EmailChipsInput";
import {
  currentOrgSelector,
  currentUserSelector,
} from "../../../common/state/selectors/authSelector";
import WixarLogo3 from "../../../common/components/Icon/WixarLogo3";

interface Props {
  build: IBuild;
}

interface ShareLink {
  id: number;
  token: string;
  label: string;
  description: string;
  linkType: "download" | "app";
  expiryDays: number;
  expiresAt: string;
  isActive: boolean;
  createdAt: string;
  createdBy: { id: number; username: string } | null;
}

interface EligibleEnduser {
  id: number;
  email: string;
  firstname: string;
  lastname: string;
}

interface SendResult {
  total: number;
  sent: number;
  failed: { email: string; reason: string }[];
}

function showQRCode(platform: string): boolean {
  return platform === "Android";
}

function isWebGLPlatform(platform: string): boolean {
  return ["WebGLApp", "WebGL App"].includes(platform);
}

const EXPIRY_PRESETS = [
  { days: 2, labelKey: "buildShare.expiry.2days" },
  { days: 7, labelKey: "buildShare.expiry.7days" },
  { days: 15, labelKey: "buildShare.expiry.15days" },
  { days: 30, labelKey: "buildShare.expiry.30days" },
  { days: 90, labelKey: "buildShare.expiry.90days" },
];

type ExpiryUnit = "hours" | "days";
const MIN_HOURS = 2;
const MAX_DAYS = 90;
const MAX_SEND_EMAILS = 50;
const MAX_SEND_MESSAGE_LEN = 500;

function customToDays(value: number, unit: ExpiryUnit): number {
  return unit === "hours" ? value / 24 : value;
}

function validateCustomExpiry(raw: string, unit: ExpiryUnit): string | null {
  if (raw === "") return "buildShare.customExpiry.errorInvalid";
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return "buildShare.customExpiry.errorInvalid";
  const days = customToDays(value, unit);
  if (days < MIN_HOURS / 24) return "buildShare.customExpiry.errorMin";
  if (days > MAX_DAYS) return "buildShare.customExpiry.errorMax";
  return null;
}

function formatDuration(days: number, t: (k: string) => string): string {
  const totalHours = Math.round(days * 24);
  if (totalHours < 24) {
    return `${totalHours}${t("buildShare.durationH")}`;
  }
  const d = Math.floor(totalHours / 24);
  const h = totalHours % 24;
  if (h === 0) return `${d}${t("buildShare.durationJ")}`;
  return `${d}${t("buildShare.durationJ")} ${h}${t("buildShare.durationH")}`;
}

function formatDateWithTime(dateStr: string, locale = "fr-FR"): string {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${time}`;
}

type ModalView = "create" | "result" | "manage" | "send";

function getPlatformIconConfig(platform: string): { containerClass: string; scale: number } {
  switch (platform) {
    case "OculusQuest":
    case "MetaQuest":
      return { containerClass: "h-5 w-20 shrink-0", scale: 0.85 };
    case "PicoNeo":
      return { containerClass: "w-5 h-5 shrink-0", scale: 0.4 };
    default:
      return { containerClass: "w-5 h-5 shrink-0", scale: 0.4 };
  }
}

const MODAL_BODY_HEIGHT = "340px";

const BuildShare = ({ build }: Props) => {
  const { t } = useTranslation();
  const currentUser = useSelector(currentUserSelector);
  const currentOrg = useSelector(currentOrgSelector);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<ModalView>("create");
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [expiryDays, setExpiryDays] = useState(30);
  const [expiryMode, setExpiryMode] = useState<"preset" | "custom">("preset");
  const [customValue, setCustomValue] = useState("30");
  const [customUnit, setCustomUnit] = useState<ExpiryUnit>("days");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [linkType, setLinkType] = useState<"download" | "app">("app");

  const hasWebGLPublicUrl = isWebGLPlatform(build.target_platform) && !!build.public_s3_url;

  const [links, setLinks] = useState<ShareLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<number | null>(null);
  const [copiedPermanent, setCopiedPermanent] = useState(false);
  const [qrOpenLinkId, setQrOpenLinkId] = useState<number | null>(null);
  const [editingLinkId, setEditingLinkId] = useState<number | null>(null);
  const [editDescription, setEditDescription] = useState("");

  // Send-email full-mode state
  const [sendingFromLink, setSendingFromLink] = useState<ShareLink | null>(null);
  const [internalIds, setInternalIds] = useState<Set<number>>(new Set());
  const [externalChips, setExternalChips] = useState<EmailChip[]>([]);
  const [sendMessage, setSendMessage] = useState("");
  const [sendSending, setSendSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [pickerSearch, setPickerSearch] = useState("");
  const [externalOpen, setExternalOpen] = useState(false);
  const [eligibleAuthType, setEligibleAuthType] = useState<string | null>(null);
  const [eligibleEndusers, setEligibleEndusers] = useState<EligibleEnduser[]>([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    fetchLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [build.id]);

  const withQR = showQRCode(build.target_platform);

  const fetchLinks = async () => {
    setLinksLoading(true);
    try {
      const res = await axios.get(`${apiUrls.buildShare.shareLinks}/${build.id}`);
      setLinks(res.data || []);
    } catch {
      // Ignore — links panel will just be empty
    } finally {
      setLinksLoading(false);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setShareUrl(null);
    try {
      const finalExpiryDays =
        expiryMode === "custom" ? customToDays(Number(customValue), customUnit) : expiryDays;
      const res = await axios.post(apiUrls.buildShare.generateShareToken, {
        data: {
          buildId: build.id,
          expiryDays: finalExpiryDays,
          label: label.trim() || undefined,
          description: description.trim() || undefined,
          linkType: hasWebGLPublicUrl ? linkType : "download",
        },
      });
      setShareUrl(`${window.location.origin}/download/${res.data.token}`);
      setView("result");
      fetchLinks();
    } catch {
      setError(t("buildShare.errorGenerating"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (linkId: number) => {
    try {
      const res = await axios.put(`${apiUrls.buildShare.shareLinks}/${linkId}/toggle`);
      setLinks((prev) =>
        prev.map((l) => (l.id === linkId ? { ...l, isActive: res.data.isActive } : l)),
      );
    } catch {
      // Ignore
    }
  };

  const handleUpdateDescription = async (linkId: number) => {
    try {
      await axios.put(`${apiUrls.buildShare.shareLinks}/${linkId}/update`, {
        data: { description: editDescription.trim() },
      });
      setLinks((prev) =>
        prev.map((l) => (l.id === linkId ? { ...l, description: editDescription.trim() } : l)),
      );
      setEditingLinkId(null);
    } catch {
      // Ignore
    }
  };

  const handleDelete = async (linkId: number) => {
    try {
      await axios.delete(`${apiUrls.buildShare.shareLinks}/${linkId}`);
      setLinks((prev) => prev.filter((l) => l.id !== linkId));
    } catch {
      // Ignore
    }
  };

  const handleCopyLink = async (linkId: number, url: string) => {
    await copyToClipboard(url);
    setCopiedLinkId(linkId);
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  const handleCopy = async (url?: string) => {
    const textToCopy = url || shareUrl;
    if (!textToCopy) return;
    await copyToClipboard(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyPermanent = async () => {
    if (!build.public_s3_url) return;
    await copyToClipboard(build.public_s3_url);
    setCopiedPermanent(true);
    setTimeout(() => setCopiedPermanent(false), 2000);
  };

  const fetchEligibleEndusers = async (linkId: number) => {
    setEligibleLoading(true);
    try {
      const res = await axios.get(apiUrls.buildShare.eligibleEndusers(linkId));
      setEligibleAuthType(res.data?.authType ?? null);
      setEligibleEndusers(res.data?.endusers || []);
    } catch {
      setEligibleAuthType(null);
      setEligibleEndusers([]);
    } finally {
      setEligibleLoading(false);
    }
  };

  const resetSendState = () => {
    setInternalIds(new Set());
    setExternalChips([]);
    setSendMessage("");
    setSendError(null);
    setSendResult(null);
    setPickerSearch("");
    setExternalOpen(false);
    setEligibleAuthType(null);
    setEligibleEndusers([]);
    setPreviewOpen(false);
  };

  const enterSendMode = (link: ShareLink) => {
    resetSendState();
    setSendingFromLink(link);
    setView("send");
    fetchEligibleEndusers(link.id);
  };

  const exitSendMode = () => {
    resetSendState();
    setSendingFromLink(null);
    setView("manage");
  };

  const toggleEnduser = (userId: number) => {
    setInternalIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // If a manually typed email matches an eligible enduser, promote it to
  // the picker selection rather than keeping a duplicate external chip.
  const setExternalChipsPromoting = (next: EmailChip[]) => {
    const remaining: EmailChip[] = [];
    let promoted: Set<number> | null = null;
    for (const chip of next) {
      if (chip.isValid && !chip.isDuplicate) {
        const match = eligibleEndusers.find((u) => u.email.toLowerCase() === chip.email);
        if (match) {
          if (!promoted) promoted = new Set(internalIds);
          promoted.add(match.id);
          continue;
        }
      }
      remaining.push(chip);
    }
    setExternalChips(remaining);
    if (promoted) setInternalIds(promoted);
  };

  const handleSendEmail = async () => {
    if (!sendingFromLink) return;
    setSendError(null);
    setSendResult(null);
    const internalEmails = eligibleEndusers
      .filter((u) => internalIds.has(u.id))
      .map((u) => u.email.toLowerCase());
    const externalEmails = externalChips
      .filter((c) => c.isValid && !c.isDuplicate)
      .map((c) => c.email);
    const validEmails = Array.from(new Set([...internalEmails, ...externalEmails]));
    if (validEmails.length === 0) {
      setSendError(t("buildShare.sendEmail.errorNoEmails"));
      return;
    }
    setSendSending(true);
    try {
      const res = await axios.post(apiUrls.buildShare.sendEmail(sendingFromLink.id), {
        data: {
          emails: validEmails,
          personalMessage: sendMessage.trim() || undefined,
        },
      });
      const sent: number = res.data?.sent ?? 0;
      const failed = (res.data?.failed || []) as { email: string; reason: string }[];
      setSendResult({ total: validEmails.length, sent, failed });
      setInternalIds(new Set());
      setExternalChips([]);
      setSendMessage("");
    } catch {
      setSendError(t("buildShare.sendEmail.errorGeneric"));
    } finally {
      setSendSending(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setView("create");
    setShareUrl(null);
    setCopied(false);
    setError(null);
    setLabel("");
    setDescription("");
    setExpiryMode("preset");
    setCustomValue("30");
    setCustomUnit("days");
    setEditingLinkId(null);
    setSendingFromLink(null);
    resetSendState();
    fetchLinks();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const frontendUrl = window.location.origin;

  const isExpired = (expiresAt: string) => new Date(expiresAt).getTime() < Date.now();

  const activeCount = links.filter((l) => l.isActive && !isExpired(l.expiresAt)).length;

  const isPermanentMode =
    hasWebGLPublicUrl && linkType === "app" && expiryMode === "preset" && expiryDays === 0;

  const expiryPresets =
    hasWebGLPublicUrl && linkType === "app"
      ? [{ days: 0, labelKey: "buildShare.expiry.permanent" }, ...EXPIRY_PRESETS]
      : EXPIRY_PRESETS;
  const expiryGridCols = expiryPresets.length <= 5 ? "grid-cols-5" : "grid-cols-6";

  const customExpiryError =
    expiryMode === "custom" ? validateCustomExpiry(customValue, customUnit) : null;
  const canGenerate = expiryMode === "preset" || !customExpiryError;

  return (
    <>
      <button
        onClick={handleOpen}
        className="btn-primary-outline relative inline-flex items-center gap-1.5 !px-2.5 !py-1 !min-w-0 !text-xs"
        title={t("buildShare.shareButton")}
      >
        <ShareIcon className="w-3.5 h-3.5" />
        <span>{t("buildShare.shareButton")}</span>
        {activeCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-green-500 text-white text-[10px] font-bold leading-none px-1 border-2 border-white">
            {activeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <ModalShell
          title={
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                <ShareIcon className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <span className="block text-sm font-semibold text-gray-800">
                  {t("buildShare.shareButton")}
                </span>
                <span className="block text-xs font-normal text-gray-400">V{build.version}</span>
              </div>
            </div>
          }
          width={view === "send" && previewOpen ? "52rem" : "28rem"}
          bodyHeight={MODAL_BODY_HEIGHT}
          flexibleHeight
          onClose={handleClose}
        >
          <div className="flex flex-col gap-3 w-full" style={{ minHeight: MODAL_BODY_HEIGHT }}>
            {/* Tab bar — hidden in send mode (modal is in a focused sub-flow) */}
            {view !== "send" && (
              <div className="flex border-b border-gray-200 shrink-0">
                <button
                  onClick={() => {
                    setView("create");
                    setShareUrl(null);
                  }}
                  className={`flex-1 pb-2 text-xs font-medium border-b-2 transition-colors ${
                    view === "create" || view === "result"
                      ? "border-green-500 text-green-600"
                      : "border-transparent text-gray-400 hover:text-gray-500"
                  }`}
                >
                  {t("buildShare.tabCreate")}
                </button>
                <button
                  onClick={() => setView("manage")}
                  className={`flex-1 pb-2 text-xs font-medium border-b-2 transition-colors ${
                    view === "manage"
                      ? "border-green-500 text-green-600"
                      : "border-transparent text-gray-400 hover:text-gray-500"
                  }`}
                >
                  {t("buildShare.tabManage")}
                  {links.length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px]">
                      {links.length}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* ─── Create / Result view ─── */}
            {(view === "create" || view === "result") && (
              <div className="flex flex-col gap-3 flex-1 min-h-0">
                {(() => {
                  const iconCfg = getPlatformIconConfig(build.target_platform);
                  return (
                    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-100 rounded-lg shrink-0">
                      <div
                        className={`${iconCfg.containerClass} flex items-center justify-center overflow-hidden`}
                      >
                        <div
                          style={{
                            transform: `scale(${iconCfg.scale})`,
                            transformOrigin: "center",
                          }}
                        >
                          <PlatformIcon
                            platform={build.target_platform}
                            scormVersion={build.scorm_version}
                            iconProps={{ fill: "#374151", width: "50px", height: "50px" }}
                            inTable
                          />
                        </div>
                      </div>
                      <span className="text-xs text-gray-600 truncate">
                        {build.target_platform} &middot; V{build.version}
                      </span>
                    </div>
                  );
                })()}

                {view === "create" && !loading && (
                  <>
                    {hasWebGLPublicUrl && (
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                          {t("buildShare.linkTypeLabel")}
                        </label>
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            onClick={() => setLinkType("app")}
                            className={`py-2 px-2 rounded-lg text-xs font-medium transition-all text-center ${
                              linkType === "app"
                                ? "bg-green-500 text-white shadow-sm"
                                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                          >
                            {t("buildShare.linkTypeApp")}
                          </button>
                          <button
                            onClick={() => setLinkType("download")}
                            className={`py-2 px-2 rounded-lg text-xs font-medium transition-all text-center ${
                              linkType === "download"
                                ? "bg-green-500 text-white shadow-sm"
                                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                          >
                            {t("buildShare.linkTypeDownload")}
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-400 leading-tight">
                          {linkType === "app"
                            ? t("buildShare.linkTypeAppHint")
                            : t("buildShare.linkTypeDownloadHint")}
                        </p>
                      </div>
                    )}

                    <input
                      type="text"
                      value={isPermanentMode ? "" : label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder={t("buildShare.labelPlaceholder")}
                      disabled={isPermanentMode}
                      className={`w-full px-3 py-2 text-xs border rounded-lg shrink-0 transition-colors ${
                        isPermanentMode
                          ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed placeholder-gray-300"
                          : "border-gray-200 bg-white focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 placeholder-gray-300"
                      }`}
                      maxLength={60}
                    />

                    <textarea
                      value={isPermanentMode ? "" : description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("buildShare.descriptionPlaceholder")}
                      disabled={isPermanentMode}
                      rows={2}
                      className={`w-full px-3 py-2 text-xs border rounded-lg shrink-0 transition-colors resize-none ${
                        isPermanentMode
                          ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed placeholder-gray-300"
                          : "border-gray-200 bg-white focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 placeholder-gray-300"
                      }`}
                      maxLength={200}
                    />

                    <div className="flex flex-col gap-1.5 shrink-0">
                      <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                        {t("buildShare.expiryLabel")}
                      </label>

                      <div className={`grid ${expiryGridCols} gap-1.5`}>
                        {expiryPresets.map((opt) => (
                          <button
                            key={opt.days}
                            onClick={() => {
                              setExpiryDays(opt.days);
                              setExpiryMode("preset");
                              if (opt.days > 0) {
                                setCustomValue(String(opt.days));
                                setCustomUnit("days");
                              }
                            }}
                            className={`py-1.5 rounded-lg text-xs font-medium transition-all ${
                              expiryMode === "preset" && expiryDays === opt.days
                                ? "bg-green-500 text-white shadow-sm"
                                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                          >
                            {t(opt.labelKey)}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={customUnit === "hours" ? MIN_HOURS : 1}
                          max={customUnit === "hours" ? MAX_DAYS * 24 : MAX_DAYS}
                          value={customValue}
                          placeholder={t("buildShare.customExpiry.placeholder")}
                          onFocus={() => setExpiryMode("custom")}
                          onChange={(e) => {
                            setExpiryMode("custom");
                            setCustomValue(e.target.value);
                          }}
                          className={`flex-1 px-3 py-1.5 text-xs border rounded-lg focus:outline-none transition-colors ${
                            expiryMode === "custom" && customExpiryError
                              ? "border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-400"
                              : expiryMode === "custom"
                              ? "border-green-400 focus:ring-1 focus:ring-green-400"
                              : "border-gray-200 focus:border-green-400 focus:ring-1 focus:ring-green-400"
                          }`}
                        />
                        <SegmentedControl<"hours" | "days">
                          options={[
                            { value: "hours", label: "h" },
                            { value: "days", label: "j" },
                          ]}
                          value={customUnit}
                          onChange={(unit) => {
                            setExpiryMode("custom");
                            setCustomUnit(unit);
                          }}
                          size="sm"
                          variant="outlined"
                        />
                      </div>
                      {expiryMode === "custom" && customExpiryError && (
                        <p className="text-[10px] text-red-400">{t(customExpiryError)}</p>
                      )}
                    </div>

                    <div className="mt-auto pt-2 shrink-0">
                      {isPermanentMode ? (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 px-2.5 py-2 bg-slate-100 rounded-lg text-[11px] text-gray-500 truncate font-mono select-all">
                              {build.public_s3_url}
                            </div>
                            <button
                              onClick={handleCopyPermanent}
                              className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                                copiedPermanent
                                  ? "bg-green-500 text-white"
                                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                              }`}
                            >
                              {copiedPermanent ? (
                                <CheckIcon className="w-3.5 h-3.5" />
                              ) : (
                                <CopyIcon className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                          <p className="text-[10px] text-gray-400">
                            {t("buildShare.permanentLinkHint")}
                          </p>
                        </div>
                      ) : (
                        <button
                          onClick={handleGenerate}
                          disabled={!canGenerate}
                          className="btn-primary-fill w-full !text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t("buildShare.generateLink")}
                        </button>
                      )}
                    </div>
                  </>
                )}

                {loading && (
                  <div className="flex items-center justify-center gap-2 flex-1 text-gray-400 text-sm">
                    <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                    {t("buildShare.generating")}
                  </div>
                )}

                {error && <p className="text-red-500 text-sm text-center py-2">{error}</p>}

                {view === "result" && shareUrl && (
                  <>
                    {withQR && (
                      <div className="flex justify-center">
                        <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                          <QRCodeSVG value={shareUrl} size={180} level="M" />
                        </div>
                      </div>
                    )}

                    <div className="w-full px-3 py-2.5 bg-slate-100 rounded-lg text-xs text-gray-500 break-all select-all font-mono leading-relaxed">
                      {shareUrl}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopy()}
                        className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                          copied ? "bg-green-500 text-white" : "btn-primary-fill"
                        }`}
                      >
                        {copied ? (
                          <span className="inline-flex items-center gap-1.5 justify-center">
                            <CheckIcon className="w-4 h-4" />
                            {t("buildShare.linkCopied")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 justify-center">
                            <CopyIcon className="w-3.5 h-3.5" />
                            {t("buildShare.copyLink")}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setView("create");
                          setShareUrl(null);
                        }}
                        className="btn-alternative-outline !min-w-0 !px-3"
                        title={t("buildShare.changeExpiry")}
                      >
                        <RefreshIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ─── Manage view ─── */}
            {view === "manage" && (
              <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto">
                {linksLoading && (
                  <div className="flex items-center justify-center flex-1 text-gray-400 text-sm">
                    <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}

                {!linksLoading && links.length === 0 && (
                  <div className="flex items-center justify-center flex-1">
                    <p className="text-sm text-gray-400">{t("buildShare.noLinks")}</p>
                  </div>
                )}

                {!linksLoading &&
                  links.map((link) => {
                    const expired = isExpired(link.expiresAt);
                    const expiryDate = formatDateWithTime(link.expiresAt);
                    const createdDate = formatDateWithTime(link.createdAt);
                    const linkUrl = `${frontendUrl}/download/${link.token}`;
                    const isQrOpen = qrOpenLinkId === link.id;
                    const canSendForLink = link.isActive && !expired;

                    return (
                      <div
                        key={link.id}
                        className={`rounded-lg border transition-colors ${
                          !link.isActive || expired
                            ? "bg-gray-50 border-gray-100 opacity-60"
                            : "bg-white border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-3 p-3">
                          <div
                            className={`w-2 h-2 rounded-full shrink-0 ${
                              expired
                                ? "bg-gray-300"
                                : link.isActive
                                ? "bg-green-500"
                                : "bg-red-400"
                            }`}
                          />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-gray-700 truncate">
                                {link.label || formatDuration(link.expiryDays, t)}
                              </span>
                              {link.linkType === "app" && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-blue-500">
                                  {t("buildShare.linkTypeAppShort")}
                                </span>
                              )}
                              {expired && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">
                                  {t("buildShare.expired")}
                                </span>
                              )}
                              {!expired && !link.isActive && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-500">
                                  {t("buildShare.disabled")}
                                </span>
                              )}
                            </div>
                            {link.description && editingLinkId !== link.id && (
                              <p className="text-[10px] text-gray-500 italic mt-0.5 truncate">
                                {link.description}
                              </p>
                            )}
                            <span className="text-[10px] text-gray-400">
                              {t("buildShare.createdOn")} {createdDate} &middot;{" "}
                              {t("buildShare.expiresOn")} {expiryDate}
                            </span>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            {canSendForLink && (
                              <button
                                onClick={() => enterSendMode(link)}
                                className="inline-flex items-center gap-1 px-2 h-7 rounded-md text-[11px] font-medium text-green-600 border border-green-200 bg-green-50 hover:bg-green-100 transition-colors"
                                title={t("buildShare.sendEmail.button")}
                              >
                                <MailIcon className="w-3 h-3" />
                                <span>{t("buildShare.sendEmail.buttonShort")}</span>
                              </button>
                            )}
                            {!expired && (
                              <>
                                <button
                                  onClick={() => {
                                    if (editingLinkId === link.id) {
                                      setEditingLinkId(null);
                                    } else {
                                      setEditingLinkId(link.id);
                                      setEditDescription(link.description || "");
                                    }
                                  }}
                                  className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                                    editingLinkId === link.id
                                      ? "text-green-500 bg-green-50"
                                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                  }`}
                                  title={t("buildShare.editDescription")}
                                >
                                  <PencilIcon className="w-3.5 h-3.5" />
                                </button>
                                {withQR && link.isActive && (
                                  <button
                                    onClick={() => setQrOpenLinkId(isQrOpen ? null : link.id)}
                                    className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                                      isQrOpen
                                        ? "text-green-500 bg-green-50"
                                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                    }`}
                                    title={t("buildShare.showQR")}
                                  >
                                    <QRIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleCopyLink(link.id, linkUrl)}
                                  className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                                    copiedLinkId === link.id
                                      ? "text-green-500 bg-green-50"
                                      : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                  }`}
                                  title={t("buildShare.copyLink")}
                                >
                                  {copiedLinkId === link.id ? (
                                    <CheckIcon className="w-3.5 h-3.5" />
                                  ) : (
                                    <CopyIcon className="w-3.5 h-3.5" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleToggle(link.id)}
                                  className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                                    link.isActive
                                      ? "text-green-500 hover:bg-green-50"
                                      : "text-gray-400 hover:bg-gray-100"
                                  }`}
                                  title={
                                    link.isActive
                                      ? t("buildShare.deactivate")
                                      : t("buildShare.activate")
                                  }
                                >
                                  {link.isActive ? (
                                    <ToggleOnIcon className="w-5 h-5" />
                                  ) : (
                                    <ToggleOffIcon className="w-5 h-5" />
                                  )}
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(link.id)}
                              className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title={t("buildShare.delete")}
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {editingLinkId === link.id && (
                          <div className="px-3 pb-3 pt-1 border-t border-gray-100">
                            <textarea
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              placeholder={t("buildShare.descriptionPlaceholder")}
                              rows={2}
                              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 placeholder-gray-300 resize-none"
                              maxLength={200}
                            />
                            <div className="flex justify-end gap-1.5 mt-1.5">
                              <button
                                onClick={() => setEditingLinkId(null)}
                                className="px-2.5 py-1 text-[10px] text-gray-400 hover:text-gray-600 rounded transition-colors"
                              >
                                {t("general.cancel")}
                              </button>
                              <button
                                onClick={() => handleUpdateDescription(link.id)}
                                className="px-2.5 py-1 text-[10px] bg-green-500 text-white rounded hover:bg-green-600 transition-colors font-medium"
                              >
                                {t("general.save")}
                              </button>
                            </div>
                          </div>
                        )}

                        {isQrOpen && (
                          <div className="flex flex-col items-center gap-2 px-3 pb-3 pt-1 border-t border-gray-100">
                            <div className="bg-white p-2.5 rounded-lg border border-gray-100 shadow-sm">
                              <QRCodeSVG value={linkUrl} size={140} level="M" />
                            </div>
                            <div className="w-full px-2 py-1.5 bg-slate-100 rounded text-[10px] text-gray-500 break-all select-all font-mono text-center">
                              {linkUrl}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {/* ─── Send view (full mode) ─── */}
            {view === "send" && sendingFromLink && (
              <SendView
                link={sendingFromLink}
                build={build}
                inviterFirstName={currentUser?.firstname || ""}
                inviterLastName={currentUser?.lastname || ""}
                organizationName={currentOrg?.name || ""}
                projectGroupName={build.project_group?.name || ""}
                t={t}
                shareUrl={`${frontendUrl}/download/${sendingFromLink.token}`}
                internalIds={internalIds}
                setInternalIds={setInternalIds}
                externalChips={externalChips}
                setExternalChips={setExternalChipsPromoting}
                externalOpen={externalOpen}
                setExternalOpen={setExternalOpen}
                sendMessage={sendMessage}
                setSendMessage={setSendMessage}
                sendSending={sendSending}
                sendError={sendError}
                sendResult={sendResult}
                pickerSearch={pickerSearch}
                setPickerSearch={setPickerSearch}
                toggleEnduser={toggleEnduser}
                eligibleAuthType={eligibleAuthType}
                eligibleEndusers={eligibleEndusers}
                eligibleLoading={eligibleLoading}
                previewOpen={previewOpen}
                setPreviewOpen={setPreviewOpen}
                onBack={exitSendMode}
                onClose={handleClose}
                onSend={handleSendEmail}
                onResetResult={() => setSendResult(null)}
              />
            )}
          </div>
        </ModalShell>
      )}
    </>
  );
};

/* ─── Send view (full mode) ─── */

interface SendViewProps {
  link: ShareLink;
  build: IBuild;
  inviterFirstName: string;
  inviterLastName: string;
  organizationName: string;
  projectGroupName: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  shareUrl: string;
  internalIds: Set<number>;
  setInternalIds: (s: Set<number>) => void;
  externalChips: EmailChip[];
  setExternalChips: (chips: EmailChip[]) => void;
  externalOpen: boolean;
  setExternalOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  sendMessage: string;
  setSendMessage: (msg: string) => void;
  sendSending: boolean;
  sendError: string | null;
  sendResult: SendResult | null;
  pickerSearch: string;
  setPickerSearch: (s: string) => void;
  toggleEnduser: (id: number) => void;
  eligibleAuthType: string | null;
  eligibleEndusers: EligibleEnduser[];
  eligibleLoading: boolean;
  previewOpen: boolean;
  setPreviewOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  onBack: () => void;
  onClose: () => void;
  onSend: () => void;
  onResetResult: () => void;
}

const SendView = ({
  link,
  build,
  inviterFirstName,
  inviterLastName,
  organizationName,
  projectGroupName,
  t,
  shareUrl,
  internalIds,
  setInternalIds,
  externalChips,
  setExternalChips,
  externalOpen,
  setExternalOpen,
  sendMessage,
  setSendMessage,
  sendSending,
  sendError,
  sendResult,
  pickerSearch,
  setPickerSearch,
  toggleEnduser,
  eligibleAuthType,
  eligibleEndusers,
  eligibleLoading,
  previewOpen,
  setPreviewOpen,
  onBack,
  onClose,
  onSend,
  onResetResult,
}: SendViewProps) => {
  const externalValidCount = externalChips.filter((c) => c.isValid && !c.isDuplicate).length;
  const invalidCount = externalChips.filter((c) => !c.isValid || c.isDuplicate).length;
  const validCount = internalIds.size + externalValidCount;
  const inviterFullName =
    `${inviterFirstName} ${inviterLastName}`.trim() ||
    t("buildShare.sendEmail.preview.fallbackSender");
  const linkLabel = link.label || formatDuration(link.expiryDays, t);
  const expiryDate = formatDateWithTime(link.expiresAt);

  if (sendResult) {
    const partial = sendResult.failed.length > 0;
    return (
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        <div
          className={`flex flex-col items-center text-center gap-3 p-6 rounded-xl border ${
            partial ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"
          }`}
        >
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center ${
              partial ? "bg-amber-100" : "bg-green-100"
            }`}
          >
            <CheckIcon className={`w-6 h-6 ${partial ? "text-amber-600" : "text-green-600"}`} />
          </div>
          <div className="flex flex-col gap-1">
            <span
              className={`text-sm font-semibold ${partial ? "text-amber-700" : "text-green-700"}`}
            >
              {partial
                ? t("buildShare.sendEmail.successPartial", {
                    ok: sendResult.sent,
                    failed: sendResult.failed.length,
                  })
                : t("buildShare.sendEmail.successAll", { count: sendResult.sent })}
            </span>
            <span className="text-[11px] text-gray-500">
              {t("buildShare.sendEmail.successHint", { link: linkLabel })}
            </span>
          </div>
        </div>

        {sendResult.failed.length > 0 && (
          <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-[120px] overflow-y-auto">
            {sendResult.failed.map((f, idx) => (
              <div
                key={`${f.email}-${idx}`}
                className="flex items-center justify-between px-2.5 py-1.5 text-[11px]"
              >
                <span className="text-gray-700 truncate">{f.email}</span>
                <span className="text-red-500 ml-2 shrink-0">
                  {t(`buildShare.sendEmail.reason.${f.reason}`, { defaultValue: f.reason })}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2 mt-auto pt-2">
          <button
            onClick={onResetResult}
            className="btn-alternative-outline flex-1 !text-sm !min-w-0"
          >
            {t("buildShare.sendEmail.sendMore")}
          </button>
          <button onClick={onClose} className="btn-primary-fill flex-1 !text-sm">
            {t("buildShare.sendEmail.done")}
          </button>
        </div>
      </div>
    );
  }

  const [selectedOnly, setSelectedOnly] = useState(false);
  useEffect(() => {
    if (internalIds.size === 0 && selectedOnly) setSelectedOnly(false);
  }, [internalIds, selectedOnly]);

  const eligibleFiltered = (() => {
    const q = pickerSearch.trim().toLowerCase();
    let list = eligibleEndusers;
    if (q) {
      list = list.filter((u) => {
        const name = `${u.firstname} ${u.lastname}`.toLowerCase();
        return name.includes(q) || u.email.toLowerCase().includes(q);
      });
    }
    if (selectedOnly) {
      list = list.filter((u) => internalIds.has(u.id));
    }
    // Stable sort: selected first
    return [...list].sort((a, b) => Number(internalIds.has(b.id)) - Number(internalIds.has(a.id)));
  })();

  const hasPicker = eligibleAuthType === "classic" && eligibleEndusers.length > 0;
  const allFilteredSelected =
    eligibleFiltered.length > 0 && eligibleFiltered.every((u) => internalIds.has(u.id));
  const handleToggleAll = () => {
    const filterIds = new Set(eligibleFiltered.map((u) => u.id));
    const next = new Set<number>();
    if (allFilteredSelected) {
      internalIds.forEach((id) => {
        if (!filterIds.has(id)) next.add(id);
      });
    } else {
      internalIds.forEach((id) => next.add(id));
      for (const u of eligibleFiltered) next.add(u.id);
    }
    setInternalIds(next);
  };

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      {/* Header — back + context */}
      <div className="flex items-center justify-between gap-3 shrink-0 border-b border-gray-100 pb-2">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ChevronLeftIcon className="w-3.5 h-3.5" />
          {t("buildShare.sendEmail.back")}
        </button>
        <div className="flex items-center gap-2 text-[11px] text-gray-500 truncate">
          <MailIcon className="w-3.5 h-3.5 text-green-500 shrink-0" />
          <span className="truncate font-medium text-gray-700">{linkLabel}</span>
          {link.linkType === "app" && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-blue-500 shrink-0">
              {t("buildShare.linkTypeAppShort")}
            </span>
          )}
        </div>
      </div>

      {/* Body — preview is opt-in. Form column is locked at a fixed width so
          opening/closing the preview never reflows the composer. */}
      <div
        className={`grid ${
          previewOpen ? "sm:grid-cols-[25.5rem_1fr]" : "grid-cols-1"
        } gap-4 flex-1 min-h-0`}
      >
        {/* ─ Left: recipients + message ─ */}
        <div className="flex flex-col gap-2 min-w-0">
          {/* Recipients header — label + live count */}
          <div className="flex items-center justify-between px-0.5 pb-1.5 border-b border-gray-100">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-600">
              <UsersIcon className="w-3.5 h-3.5 text-gray-400" />
              {t("buildShare.sendEmail.recipientsLabel")}
            </span>
            <span
              className={`text-[10px] ${
                validCount > 0 ? "text-emerald-600 font-medium" : "text-gray-400"
              }`}
            >
              {t("buildShare.sendEmail.selectedCount", { count: validCount })}
            </span>
          </div>

          {/* Always-visible picker (classic auth + endusers exist) */}
          {hasPicker && (
            <>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder={t("buildShare.sendEmail.pickerSearchPlaceholder")}
                  className="flex-1 min-w-0 px-2.5 py-1.5 text-[11px] border border-gray-200 rounded-md bg-white focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 placeholder-gray-300"
                />
                {eligibleFiltered.length > 0 && (
                  <button
                    type="button"
                    onClick={handleToggleAll}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md border border-emerald-100 transition-colors shrink-0"
                  >
                    <BoltIcon className="w-3 h-3" />
                    {allFilteredSelected
                      ? t("buildShare.sendEmail.deselectAll", {
                          count: eligibleFiltered.length,
                        })
                      : t("buildShare.sendEmail.selectAll", {
                          count: eligibleFiltered.length,
                        })}
                  </button>
                )}
              </div>
              <SegmentedControl
                size="sm"
                variant="pill"
                value={selectedOnly ? "selected" : "all"}
                onChange={(v) => setSelectedOnly(v === "selected")}
                options={[
                  {
                    value: "all",
                    label: t("buildShare.sendEmail.filterAll"),
                    badge: eligibleEndusers.length,
                  },
                  {
                    value: "selected",
                    label: t("buildShare.sendEmail.filterSelected"),
                    badge: internalIds.size,
                    disabled: internalIds.size === 0,
                  },
                ]}
              />
              <div
                className="border border-gray-200 rounded-md bg-white overflow-y-auto"
                style={{
                  height: `${Math.min(360, Math.max(eligibleEndusers.length * 36 + 8, 120))}px`,
                }}
              >
                {eligibleFiltered.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <p className="text-[11px] text-gray-400 text-center px-3">
                      {t("buildShare.sendEmail.pickerEmptyResult")}
                    </p>
                  </div>
                ) : (
                  eligibleFiltered.map((u) => {
                    const checked = internalIds.has(u.id);
                    const fullName = `${u.firstname} ${u.lastname}`.trim() || u.email;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => toggleEnduser(u.id)}
                        disabled={sendSending}
                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-left transition-colors ${
                          checked
                            ? "bg-emerald-50/60 hover:bg-emerald-50 text-gray-700"
                            : "hover:bg-slate-50 text-gray-700"
                        } cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        <span
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                            checked ? "bg-green-500 border-green-500" : "border-gray-300"
                          }`}
                        >
                          {checked && <CheckIcon className="w-2.5 h-2.5 text-white" />}
                        </span>
                        <span className="flex flex-col min-w-0 flex-1">
                          <span className="truncate font-medium">{fullName}</span>
                          <span className="truncate text-gray-400">{u.email}</span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}

          {eligibleAuthType === "classic" && !eligibleLoading && eligibleEndusers.length === 0 && (
            <p className="text-[10px] text-gray-400 italic px-0.5">
              {t("buildShare.sendEmail.emptyEligibleHint")}
            </p>
          )}

          {!eligibleLoading && eligibleAuthType && eligibleAuthType !== "classic" && (
            <p className="text-[10px] text-gray-400 italic">
              {t(`buildShare.sendEmail.authTypeHint.${eligibleAuthType}`, { defaultValue: "" })}
            </p>
          )}

          {/* External emails — collapsed when picker is shown, inline otherwise */}
          {hasPicker ? (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => setExternalOpen((v) => !v)}
                className="self-start inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ChevronDownIcon
                  className={`w-2.5 h-2.5 transition-transform ${externalOpen ? "" : "-rotate-90"}`}
                />
                <span>{t("buildShare.sendEmail.externalToggle")}</span>
                {externalChips.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-gray-500 text-[10px]">
                    {externalChips.length}
                  </span>
                )}
              </button>
              {externalOpen && (
                <EmailChipsInput
                  value={externalChips}
                  onChange={setExternalChips}
                  maxCount={MAX_SEND_EMAILS}
                  disabled={sendSending}
                />
              )}
            </div>
          ) : (
            <EmailChipsInput
              value={externalChips}
              onChange={setExternalChips}
              maxCount={MAX_SEND_EMAILS}
              disabled={sendSending}
            />
          )}

          {invalidCount > 0 && (
            <p className="text-[10px] text-red-500">
              {t("buildShare.sendEmail.invalidCount", { count: invalidCount })}
            </p>
          )}

          <textarea
            value={sendMessage}
            onChange={(e) => setSendMessage(e.target.value)}
            placeholder={t("buildShare.sendEmail.messagePlaceholder")}
            rows={3}
            maxLength={MAX_SEND_MESSAGE_LEN}
            disabled={sendSending}
            className="w-full px-2.5 py-1.5 text-[11px] border border-gray-200 rounded-md bg-white focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 placeholder-gray-300 resize-none"
          />

          {sendMessage.trim() && (
            <p className="text-[10px] text-gray-400 italic px-0.5">
              {t("buildShare.sendEmail.messageRenderHint")}
            </p>
          )}

          {sendError && <p className="text-red-500 text-[11px]">{sendError}</p>}

          <div className="pt-1 flex flex-col gap-1.5">
            <button
              type="button"
              onClick={() => setPreviewOpen((v) => !v)}
              className="self-start inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 transition-colors"
            >
              <EyeIcon className="w-3 h-3" />
              <span>
                {previewOpen
                  ? t("buildShare.sendEmail.previewToggleHide")
                  : t("buildShare.sendEmail.previewToggleShow")}
              </span>
              <ChevronDownIcon
                className={`w-2.5 h-2.5 transition-transform ${previewOpen ? "rotate-180" : ""}`}
              />
            </button>
            <button
              onClick={onSend}
              disabled={sendSending || validCount === 0}
              className="btn-primary-fill w-full !text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendSending ? (
                <span className="inline-flex items-center gap-2 justify-center">
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t("buildShare.sendEmail.sending")}
                </span>
              ) : validCount === 0 ? (
                <span className="inline-flex items-center justify-center">
                  {t("buildShare.sendEmail.sendEmptyHint")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 justify-center">
                  <MailIcon className="w-3.5 h-3.5" />
                  {t("buildShare.sendEmail.send", { count: validCount })}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ─ Right: email preview (opt-in) ─ */}
        {previewOpen && (
          <div className="flex flex-col gap-1.5 min-w-0">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
              {t("buildShare.sendEmail.previewLabel")}
            </span>
            <div className="border border-gray-200 rounded-md bg-white overflow-hidden flex flex-col">
              {/* Mock email header (sender + subject) */}
              <div className="px-3 py-2 border-b border-gray-100 bg-slate-50 text-[10px] text-gray-500 flex flex-col gap-0.5">
                <div className="flex gap-1.5">
                  <span className="text-gray-400 shrink-0">De</span>
                  <span className="font-medium text-gray-700 truncate">
                    {inviterFullName}
                    {organizationName && ` · ${organizationName}`}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  <span className="text-gray-400 shrink-0">Objet</span>
                  <span className="text-gray-700 truncate">
                    {t("buildShare.sendEmail.preview.subject")}
                  </span>
                </div>
              </div>

              {/* Mock email body — mirrors the SendGrid template visually */}
              <div className="px-4 py-3 flex flex-col items-center gap-2.5 text-[10px] text-gray-700 leading-relaxed text-center">
                <div className="pt-1 pb-0.5">
                  <WixarLogo3 style={{ width: "100px", height: "auto" }} />
                </div>

                <p className="pt-1">{t("buildShare.sendEmail.preview.greeting")}</p>

                <p className="leading-snug">
                  <strong className="text-gray-800">{inviterFullName}</strong>
                  {" de "}
                  <strong className="text-gray-800">
                    {organizationName || t("buildShare.sendEmail.preview.fallbackOrg")}
                  </strong>
                  {" vous invite à tester "}
                  <strong className="text-gray-800">
                    {projectGroupName ||
                      build.name ||
                      t("buildShare.sendEmail.preview.fallbackApp")}
                  </strong>
                  {" sur "}
                  <strong className="text-gray-800">{build.target_platform}</strong>.
                </p>

                {sendMessage.trim() && (
                  <p className="italic text-gray-500 leading-snug border-l-2 border-gray-200 pl-2 self-stretch text-left break-words">
                    {sendMessage.trim()}
                  </p>
                )}

                <p>{t("buildShare.sendEmail.preview.clickInstruction")}</p>

                <div className="py-1">
                  <span className="inline-block px-4 py-2 rounded-md bg-red-500 text-white font-semibold text-[11px]">
                    {t("buildShare.sendEmail.preview.cta")}
                  </span>
                </div>

                <p className="text-[9px] text-gray-400">
                  {t("buildShare.sendEmail.preview.expiresOn", { date: expiryDate })}
                </p>
                <p className="text-[9px] text-gray-400 leading-snug">
                  {t("buildShare.sendEmail.preview.fallbackInstruction")}
                </p>
                <p className="text-[9px] text-blue-600 break-all">{shareUrl}</p>

                <div className="border-t border-gray-100 w-full mt-2 pt-2 flex flex-col gap-1.5">
                  <p className="text-[9px] text-gray-500">
                    {t("buildShare.sendEmail.preview.contact")}{" "}
                    <em className="text-blue-600 not-italic font-semibold">info@wixar.io</em>
                  </p>
                  <p className="text-[9px] text-gray-500 leading-snug">
                    Have fun !<br />
                    L&apos;équipe Wixar
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Inline icons ─── */

const ShareIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
    />
  </svg>
);

const CopyIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
    />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const RefreshIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M20.015 4.356v4.992"
    />
  </svg>
);

const QRIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm14 3h.01M17 14h3v3h-3v-3zm0 4h3v3h-3v-3zm-4 0h3v3h-3v-3z"
    />
  </svg>
);

const PencilIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
    />
  </svg>
);

const TrashIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const ToggleOnIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17 7H7a5 5 0 000 10h10a5 5 0 000-10zm0 8a3 3 0 110-6 3 3 0 010 6z" />
  </svg>
);

const ToggleOffIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 7h10a5 5 0 010 10H7A5 5 0 017 7zm0 8a3 3 0 100-6 3 3 0 000 6z" />
  </svg>
);

const UsersIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m10-3.13a4 4 0 11-8 0 4 4 0 018 0zm6-2a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const MailIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 11H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2z"
    />
  </svg>
);

const ChevronLeftIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const BoltIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const EyeIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const ChevronDownIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

export default BuildShare;
